<template>
  <div class="msg-view">
    <div class="message-bg-fixed"></div>
    <div class="message-bg-mask"></div>

    <!-- 简约页面标题 -->
    <div class="page-header">
      <h1 class="page-title">留言板</h1>
      <p class="page-subtitle">记录每一份美好的心情</p>
    </div>

    <!-- 内容区 -->
      <van-list
        v-model="loading"
        :finished="finished"
        @load="onLoad"
      class="msg-list"
      >
      <template v-for="g in grouped">
        <div :key="`date-${g.date}`" class="date-anchor">
          <span class="date-label">{{ g.date }}</span>
        </div>
        <MessageItem
          v-for="m in g.items"
          :key="`msg-${m.id}`"
          :message="{...m, time: formatOnlyTime(m.createdAt)}"
          @like="handleLike"
          @detail="openDetail"
          @comment="handleComment"
        />
      </template>
      <template #finished>
        <div class="no-more">
          <div class="end-icon">🌙</div>
          <p class="end-text">暂时没有更多内容了</p>
        </div>
        </template>
      </van-list>

    <!-- 右下角FAB悬浮按钮 -->
    <div class="fab-container">
      <button class="fab-button" @click="openEditor">
        <van-icon name="edit" color="#fff" size="20" />
      </button>
    </div>

    <!-- 底部弹窗写留言 -->
    <van-popup v-model="showEditor" position="bottom" round :style="{ height: '70vh' }" @open="onPopupOpen" @close="onPopupClose">
      <div class="sheet">
        <div class="sheet-header">
          <h3 class="sheet-title">写下心语</h3>
          <p class="sheet-subtitle">让文字承载思念</p>
        </div>
        <van-field
          v-model="content"
          rows="5"
          type="textarea"
          autosize
          maxlength="9999"
          placeholder="想对TA说些什么..."
          :border="false"
          show-word-limit
          class="message-input"
        />
        <button class="send-button" @click="handleSubmit" :disabled="!content.trim()">
          <span class="send-text">寄出心语</span>
        </button>
    </div>
    </van-popup>

    <!-- 右侧详情弹窗 -->
    <van-popup v-model="showDetail" position="right" :style="{ width: '85vw', height: '100%' }">
      <div class="detail-popup">
        <div class="detail-header">
          <span class="nickname">{{ detailMsg.nickname }}</span>
          <span class="time">{{ formatOnlyTime(detailMsg.createdAt) }}</span>
        </div>
        <div class="detail-content">{{ detailMsg.content }}</div>
        <div class="detail-actions">
          <van-icon :name="detailMsg.liked ? 'like' : 'like-o'" :color="detailMsg.liked ? '#f48fb1' : '#aaa'" @click="handleLike(detailMsg)" />
          <span>{{ detailMsg.likes }}</span>
        </div>
        <div class="detail-comments">
          <div v-for="(c, i) in detailMsg.comments" :key="i" class="comment-item">
            <span class="comment-nick">{{ c.nickname }}：</span>{{ c.content }}
          </div>
        </div>
      </div>
    </van-popup>
  </div>
</template>

<script>
import MessageItem from '@/components/MessageItem.vue'
import { addMessage, getMessages, likeMessage, unlikeMessage, addComment } from '@/api/message'

export default {
  name: 'MessageView',
  components: { MessageItem },
  data () {
    return {
      content: '',
      messages: [],
      showDetail: false,
      detailMsg: {},
      page: 1,
      pageSize: 12,
      loading: false,
      finished: false,
      likeLock: false,
      showEditor: false
    }
  },
  created () {
    this.resetList()
  },
  computed: {
    grouped () {
      const map = {}
      this.messages.forEach(m => {
        const d = m.createdAt.split('T')[0]
        ;(map[d] ||= []).push(m)
      })
      return Object.keys(map).sort((a, b) => b.localeCompare(a))
        .map(date => ({ date, items: map[date] }))
    }
  },
  methods: {
    formatOnlyTime (t) {
      if (!t) return ''
      const d = new Date(t)
      const h = String(d.getHours()).padStart(2, '0')
      const min = String(d.getMinutes()).padStart(2, '0')
      return `${h}:${min}`
    },
    resetList () {
      this.page = 1
      this.messages = []
      this.finished = false
      this.onLoad()
    },
    async onLoad () {
      if (this.finished) return
      this.loading = true
      try {
        const res = await getMessages({ page: this.page, size: this.pageSize })
        const list = (res.data && res.data.records) || []
        const formattedList = list.map(item => ({
          ...item,
          likeUsers: Array.isArray(item.likeUsers) ? item.likeUsers : [],
          comments: Array.isArray(item.comments) ? item.comments : []
        }))
        if (formattedList.length < this.pageSize) {
          this.finished = true
        }
        this.messages = this.messages.concat(formattedList)
        this.page++
      } catch (e) {
        this.$toast('加载失败，请稍后重试')
        this.finished = true
      }
      this.loading = false
    },
    // 🎯 打开编辑器弹窗
    openEditor () {
      this.showEditor = true
      // 不需要手动发送事件，@open会自动触发
    },
    // 🎭 弹窗打开时的处理
    onPopupOpen () {
      this.$root.$emit('show-popup') // 通知App隐藏导航栏
    },
    // 🎭 弹窗关闭时的处理
    onPopupClose () {
      this.$root.$emit('hide-popup') // 通知App显示导航栏
    },
    async handleSubmit () {
      if (!this.content.trim()) {
        this.$toast('请输入留言内容')
        return
      }
      try {
        const res = await addMessage({ content: this.content })
        if (res.code === 200) {
          this.$toast({
            message: '发布成功 ✨',
            className: 'custom-toast'
          })
          this.content = ''
          this.showEditor = false
          this.resetList()
        } else {
          this.$toast(res.message || '留言失败')
        }
      } catch (e) {
        this.$toast('留言失败，请稍后重试')
      }
    },
    async handleLike (msg) {
      if (this.likeLock) return
      this.likeLock = true
      const userId = localStorage.getItem('userId')
      const nickname = localStorage.getItem('nickname') || ''
      let arr = Array.isArray(msg.likeUsers) ? [...msg.likeUsers] : []
      // 判断当前用户是否已点赞
      const isLiked = arr.some(u => String(u.userId) === String(userId))
      // 本地只增删当前用户，保留原有点赞用户，且不重复push
      if (!isLiked) {
        arr.push({ userId, nickname })
      } else {
        arr = arr.filter(u => String(u.userId) !== String(userId))
      }
      // 再次去重，防止后端数据有重复
      arr = arr.filter((u, idx, self) => idx === self.findIndex(x => String(x.userId) === String(u.userId)))
      const i = this.messages.findIndex(m => m.id === msg.id)
      if (i !== -1) {
        this.$set(this.messages, i, { ...msg, likeUsers: arr })
      }
      if (this.detailMsg && this.detailMsg.id === msg.id) {
        this.detailMsg = { ...msg, likeUsers: arr }
      }
      // 调用API
      try {
        if (!isLiked) {
          await likeMessage(msg.id)
        } else {
          await unlikeMessage(msg.id)
        }
      } catch (e) {
        // 回滚UI
        if (!isLiked) {
          arr = arr.filter(u => String(u.userId) !== String(userId))
        } else {
          arr.push({ userId, nickname })
        }
        arr = arr.filter((u, idx, self) => idx === self.findIndex(x => String(x.userId) === String(u.userId)))
        if (i !== -1) {
          this.$set(this.messages, i, { ...msg, likeUsers: arr })
        }
        if (this.detailMsg && this.detailMsg.id === msg.id) {
          this.detailMsg = { ...msg, likeUsers: arr }
        }
        this.$toast('操作失败，请重试')
      }
      setTimeout(() => { this.likeLock = false }, 500)
    },
    openDetail (msg) {
      this.detailMsg = msg
      this.showDetail = true
    },
    async handleComment ({ message, content }) {
      try {
        const res = await addComment({ messageId: message.id, content })
        if (res.code === 200) {
          this.$toast('评论成功！')
          // 本地push一条评论
          const comment = {
            userId: localStorage.getItem('userId'),
            nickname: localStorage.getItem('nickname'),
            content,
            createdAt: new Date().toISOString()
          }
          if (Array.isArray(message.comments)) {
            message.comments.push(comment)
          } else {
            this.$set(message, 'comments', [comment])
          }
        } else {
          this.$toast(res.message || '评论失败')
        }
      } catch (e) {
        this.$toast('评论失败，请稍后重试')
      }
    }
  }
}
</script>

<style scoped>
/* 背景配置 - 方便后续更换 */
:root {
  --message-bg-image: url('~@/assets/message/message.jpg');
  --message-bg-overlay: linear-gradient(
    to bottom,
    rgba(255,255,255,0.25) 0%,
    rgba(255,255,255,0.35) 50%,
    rgba(255,255,255,0.30) 100%
  );
}

.msg-view {
  min-height: 100vh;
  width: 100vw;
  position: relative;
  z-index: 2;
  padding: 0 0 100px;
  box-sizing: border-box;
  overflow-y: auto;
  overflow-x: hidden;

  /* 🚀 苹果级滚动优化 */
  scroll-behavior: smooth; /* 平滑滚动 */
  -webkit-overflow-scrolling: touch; /* iOS 弹性滚动 */
  overscroll-behavior: contain; /* 防止过度滚动 */
  scrollbar-width: none; /* Firefox 隐藏滚动条 */
  -ms-overflow-style: none; /* IE/Edge 隐藏滚动条 */
}

/* Webkit浏览器隐藏滚动条 */
.msg-view::-webkit-scrollbar {
  display: none;
}

.message-bg-fixed {
  position: fixed;
  left: 0;
  top: 0;
  width: 100vw;
  height: 100vh;
  z-index: 0;
  background: var(--message-bg-image) center center / cover no-repeat;
  background-attachment: fixed;
  pointer-events: none;

  /* 🎯 GPU硬件加速优化 */
  will-change: transform; /* 提示浏览器即将变换 */
  transform: translateZ(0); /* 强制GPU加速 */
  backface-visibility: hidden; /* 隐藏背面，提高性能 */
}

.message-bg-mask {
  position: fixed;
  left: 0;
  top: 0;
  width: 100vw;
  height: 100vh;
  background: var(--message-bg-overlay);
  z-index: 1;
  pointer-events: none;

  /* 🎯 GPU硬件加速优化 */
  will-change: transform;
  transform: translateZ(0);
  backface-visibility: hidden;
}

/* 简约页面标题 */
.page-header {
  position: relative;
  z-index: 2;
  text-align: center;
  padding: 80px 20px 32px;
}

.page-title {
  font-size: 1.6rem;
  font-weight: 600;
  color: #2d3748;
  margin: 0 0 8px 0;
  letter-spacing: 1px;
  text-shadow: 0 2px 8px rgba(255,255,255,0.8);
}

.page-subtitle {
  font-size: 0.9rem;
  color: #718096;
  margin: 0;
  font-weight: 400;
  opacity: 0.85;
  }

.msg-list {
  width: 90vw;
  max-width: 420px;
  margin: 0 auto;
  position: relative;
  z-index: 2;

  /* ⚡ 列表滚动优化 */
  transform: translateZ(0); /* GPU加速 */
  will-change: transform; /* 优化重绘 */
}

.date-anchor {
  position: sticky;
  top: 0;
  padding: 8px 0;
  text-align: center;
  margin: 16px 0 8px;
  z-index: 3;
}

.date-label {
  display: inline-block;
  background: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(16px) saturate(180%);
  color: #667eea;
  font-size: 0.85rem;
  font-weight: 500;
  border-radius: 20px;
  padding: 4px 14px;
  letter-spacing: 0.5px;
  border: 1px solid rgba(255, 255, 255, 0.5);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
}

.no-more {
  text-align: center;
  padding: 40px 20px 20px;
  position: relative;
  z-index: 2;
}

.end-icon {
  font-size: 1.8rem;
  margin-bottom: 10px;
  opacity: 0.6;
}

.end-text {
  font-size: 0.85rem;
  color: #a0aec0;
  margin: 0;
  opacity: 0.8;
}

/* FAB悬浮按钮 - 与底部导航胶囊对齐 */
.fab-container {
  position: fixed;
  right: 24px;
  bottom: calc(env(safe-area-inset-bottom) + 15px); /* 与导航栏胶囊对齐 */
  z-index: 10;
}

.fab-button {
  width: 52px;  /* 与导航栏胶囊高度匹配 */
  height: 52px; /* 与导航栏胶囊高度匹配 */
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  border-radius: 50%;
  color: white;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 8px 24px rgba(102, 126, 234, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;

  /* ⚡ 悬浮按钮优化 */
  transform: translateZ(0); /* GPU加速 */
  will-change: transform; /* 变换优化 */
  backface-visibility: hidden; /* 隐藏背面 */
}

.fab-button::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle at center, rgba(255,255,255,0.3) 0%, transparent 70%);
  opacity: 0;
  transition: opacity 0.3s;
}

.fab-button:hover::before {
  opacity: 1;
}

.fab-button:hover {
  transform: translateY(-3px) scale(1.05);
  box-shadow: 0 12px 32px rgba(102, 126, 234, 0.4);
}

.fab-button:active {
  transform: translateY(-1px) scale(0.98);
}

/* 写留言弹窗样式 - 保持原有样式 */
.sheet {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 24px 20px;
  box-sizing: border-box;
  background: linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%);
}

.sheet-header {
  text-align: center;
  margin-bottom: 24px;
}

.sheet-title {
  font-family: '华文楷体', 'KaiTi', 'STKaiti', serif;
  font-size: 1.3rem;
  font-weight: 300;
  color: #2d3748;
  margin: 0 0 8px 0;
  letter-spacing: 2px;
}

.sheet-subtitle {
  font-family: '华文楷体', 'KaiTi', 'STKaiti', serif;
  font-size: 0.9rem;
  color: #718096;
  margin: 0;
  letter-spacing: 1px;
  opacity: 0.8;
}

::v-deep .message-input {
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.6);
  margin-bottom: 20px;
  padding: 16px;
  font-size: 1rem;
  line-height: 1.6;
  transition: all 0.3s ease;
}

::v-deep .message-input .van-field__control {
  color: #2d3748;
  font-size: 1rem;
  background: transparent;
  min-height: 120px;
}

::v-deep .message-input .van-field__control::placeholder {
  color: #a0aec0;
  font-style: italic;
}

::v-deep .message-input:focus-within {
  border: 1.5px solid #667eea;
  box-shadow: 0 4px 20px rgba(102, 126, 234, 0.15);
}

.send-button {
  margin-top: auto;
  padding: 14px 32px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  border-radius: 50px;
  color: white;
  font-size: 1.1rem;
  font-weight: 600;
  letter-spacing: 1px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 8px 24px rgba(102, 126, 234, 0.3);
  position: relative;
  overflow: hidden;
}

.send-button::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
  transition: left 0.6s;
}

.send-button:hover::before {
  left: 100%;
}

.send-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 32px rgba(102, 126, 234, 0.4);
}

.send-button:disabled {
  background: linear-gradient(135deg, #cbd5e0 0%, #a0aec0 100%);
  cursor: not-allowed;
  transform: none;
  box-shadow: 0 4px 12px rgba(203, 213, 224, 0.2);
}

.send-text {
  position: relative;
  z-index: 1;
}

/* 详情弹窗样式 */
.detail-popup {
  padding: 80px 20px 20px 20px;
  background: linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%);
  height: 100%;
  box-sizing: border-box;
}

.detail-header {
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid rgba(160, 174, 192, 0.2);
}

.nickname {
  font-weight: 600;
  color: #667eea;
  font-size: 1.1rem;
  margin-right: 12px;
}

.time {
  color: #a0aec0;
  font-size: 0.9rem;
}

.detail-content {
  font-size: 1rem;
  color: #2d3748;
  line-height: 1.7;
  margin-bottom: 20px;
  padding: 16px;
  background: rgba(255, 255, 255, 0.6);
  border-radius: 12px;
  backdrop-filter: blur(8px);
}

.detail-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 20px;
  color: #718096;
}

.detail-comments {
  background: rgba(255, 255, 255, 0.4);
  border-radius: 12px;
  padding: 16px;
  backdrop-filter: blur(8px);
}

.comment-item {
  margin-bottom: 12px;
  font-size: 0.95rem;
  line-height: 1.5;
  color: #2d3748;
}

.comment-nick {
  font-weight: 600;
  color: #667eea;
}

/* 响应式设计 */
@media (max-width: 600px) {
  .page-header {
    padding: 60px 16px 24px;
  }

  .page-title {
    font-size: 1.4rem;
  }

  .page-subtitle {
    font-size: 0.85rem;
  }

  .fab-container {
    right: 16px;
    bottom: calc(env(safe-area-inset-bottom) + 12px); /* 与移动端导航栏底部对齐 */
  }

  .fab-button {
    width: 48px;  /* 移动端稍小一些 */
    height: 48px; /* 移动端稍小一些 */
  }
}

/* 更小屏幕的进一步适配 */
@media (max-width: 480px) {
  .fab-container {
    right: 16px;
    bottom: calc(env(safe-area-inset-bottom) + 6px); /* 与最小屏幕导航栏对齐 */
  }

  .fab-button {
    width: 44px;  /* 超小屏幕更小 */
    height: 44px;
  }

  .fab-icon {
    width: 18px;
    height: 18px;
  }

  .detail-popup {
    padding: 60px 16px 16px 16px;
  }

  .sheet {
    padding: 20px 16px;
  }
}

/* 全局滚动性能优化 */
* {
  -webkit-font-smoothing: antialiased; /* 字体反锯齿 */
  -moz-osx-font-smoothing: grayscale; /* macOS字体优化 */
}

/* 关键交互元素GPU加速 */
.page-header,
.date-anchor,
.fab-container {
  transform: translateZ(0);
  will-change: transform;
}

/* 自定义Toast样式 */
::v-deep .custom-toast {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 25px;
  font-weight: 500;
  letter-spacing: 0.5px;
}

/*
  如何更换背景图：
  只需要修改 :root 中的 --message-bg-image 变量即可
  例如：
  --message-bg-image: url('path/to/your/new-image.jpg');
  或者：
  --message-bg-image: linear-gradient(135deg, #667eea, #764ba2);
*/
</style>
